/* ============================================================================
   HighDeep · pf-wx 引擎 v3 —— 日夜相位 + 真实天气物体（云朵/太阳/闪电/雾/雨雪）
   ----------------------------------------------------------------------------
   公开 API（挂 window.__hdWx）：
     __hdWx.set('kind'|'phase', value)
       kind : auto|clear|cloudy|fog|drizzle|rain|snow|thunder
       phase: auto|dawn|day|golden|dusk|night
     __hdWx.refresh() / __hdWx.on(fn) / __hdWx.state
   数据源：WeatherAPI.com 免费档（官网 Pricing 表 `Commercial Use` 明确打勾）。
           一次 forecast.json 即含：天气码 / is_day / 温度 / 云量 / 日出日落 / **该地当地时间**；
           位置按 IP（q=auto:ip）。🔴 key 是**前端公开凭据**（纯前端方案藏不住，属正常）。
   ----------------------------------------------------------------------------
   v3 相对 v2 的改动：
     · 去掉"整屏淡灰渐变"式的 amb，改为**可辨认的物体**：
       云朵(4 朵飘移) · 太阳(带旋转光芒) · 闪电(SVG 折线) · 雾带(3 条错速)
     · day 相位色罩改**透明**（"白天=现站原样"），少一层白纱 ⇒ 下面物体对比度上来
     · 夜色中段留薄（.54）—— v2 教训：0.74 的均匀深罩会把下面所有天气压平
   🔴🔴 速度用 CSS px/秒（唯一真源）；缓冲区尺寸只认元素自身 getBoundingClientRect()
       —— 详见 v2 注释（用户报过"有些设备速度很快"）。
   ========================================================================== */
(function () {
  'use strict';
  if (window.__hdWx) return;

  var D = document, W = window, root = D.documentElement;
  var NS = 'http://www.w3.org/2000/svg';

  var REDUCE = false;
  try { REDUCE = !!(W.matchMedia && W.matchMedia('(prefers-reduced-motion: reduce)').matches); } catch (e) {}

  var SEARCH = String(W.location.search || '');
  var OFF = root.getAttribute('data-pfm') === 'off' ||
            /(?:^|[?&])(?:pfm|wx)=off(?:&|$)/.test(SEARCH);

  var PHASES = ['dawn', 'day', 'golden', 'dusk', 'night'];
  var KINDS = ['clear', 'cloudy', 'drizzle', 'rain', 'snow', 'thunder'];   /* v24：'fog' 从 KINDS 移除 ⇒ 控制条无入口、自动模式也不会选它（实现仍完整保留，可由 __hdWx.set('kind','fog') 手动触发） */
  var PHASE_CN = { dawn: '拂晓', day: '白天', golden: '黄金时刻', dusk: '黄昏', night: '夜晚' };
  var KIND_CN = { auto: '自动', clear: '晴', cloudy: '多云', drizzle: '毛毛雨', rain: '雨', snow: '雪', thunder: '雷暴' };

  /* 🔴 每种天气点亮哪些"物体"。数字 = 点亮几朵云 / 几条雾带。 */
  var PLAN = {
    clear:   { sun: 1 },
    cloudy:  { cloud: 4, sun: 1 },   /* v9：多云本就常"太阳穿云"，原来白天没太阳 */
    fog:     { fog: 4, cloud: 1 },
    drizzle: { cloud: 2, particles: 1 },
    rain:    { cloud: 3, particles: 1 },
    snow:    { cloud: 2, particles: 1 },
    thunder: { cloud: 4, bolt: 1, particles: 1 }
  };

  /* 云朵布点（top 用视口百分比；宽度/高度 px；dur 秒；delay 负值 = 错开起点） */
  var CLOUDS = [
    /* 🔴 v11：用户「多云，顶部应该云飘过」⇒ 4 朵收到视口上方。
       🔴 v14：用户「云再低一点」⇒ 整体下移 6 个百分点（2/12/6/17 → 8/18/12/23）。
       原来 top 2~6% 的两朵有一多半被顶栏（约占 12%）压住，下移后完整露出。 */
    { w: 620, h: 205, top: 8,  dur: 92,  delay: -14 },
    { w: 470, h: 160, top: 18, dur: 126, delay: -52 },
    { w: 800, h: 255, top: 12, dur: 160, delay: -108 },
    { w: 540, h: 180, top: 23, dur: 74,  delay: -34 }
  ];
  /* 🔴 v6：原来 250/330/380px 高、位于 23/47/70% ⇒ **互相重叠、从上到下连续覆盖**，
     观感只是"整页被冲淡"，读不出是雾（用户：「雾效果不好」）。
     改成**矮一档 + 拉开间距**（30/52/76%），配合 CSS 里压扁的椭圆 ⇒ 有间隙才像"飘动的雾"。
     ⚠️ 第一条仍留在 30%（历史教训：原来全在下半屏 ⇒ 首屏看不见）。 */
  /* 🔴 v7：三条**左右错开**（x）+ 最低一条加厚成"地面积雾"。
     原来三条都水平居中 ⇒ 叠起来仍是一条条横贯全屏的带；而"底部厚、往上薄"才是雾最好认的形态。 */
  /* 🔴 v8：最高一条原来在 27%，而顶栏＋公告栏占了上方约 11% ⇒ 用户："雾气在网页顶部并没有图像"。
     现在 4 条，最高一条落在 **12%**（= 顶栏下方的可见区顶端），往下依次 34/56/76%。 */
  /* 🔴 v14：用户「雾再低一点」⇒ 4 条整体下移（12/34/56/76 → 17/38/60/80）。
     第一条从 12%（143px，紧贴顶栏下缘）挪到 17%（203px）⇒ 顶部留出呼吸空间。 */
  var FOGBANDS = [
    { h: 120, top: 17, dur: 27, x: -7 },
    { h: 145, top: 38, dur: 35, x: 12 },
    { h: 175, top: 60, dur: 44, x: -5 },
    { h: 235, top: 80, dur: 53, x: 8 }
  ];

  /* 🔴 速度真源：CSS px/秒 */
  var SPEED = {
    /* 🔴 毛毛雨：慢 + 短 + **断续**（见 draw() 的 DRIZ 分支）——
       靠"形态"与"雨"分开，而不是只把雨的参数调淡。长度单位 = 缓冲 px（屏幕 ×2）。 */
    /* 🔴 v35（用户选「乙」游戏雨痕）：倾斜角从 ≈8.9° 提到 **16° 左右**（vx/vy≈0.28），
       长度加长近一倍、速度加快 ⇒ 才有"雨"的斜长拖尾感，而不是一根根垂直的小棍子。 */
    /* 🔴 v36（用户选「丁」：Stripe / Linear 那种高端站氛围雨）：
       速度整体放缓约 40% ⇒ 不再是"砸下来"的暴雨感，而是**飘着的细雨**；
       倾斜角维持 ~16°（vx/vy≈0.30）。配合下面 buildStreaks 里的低透明度 + 细线 + 弱光晕，
       整体目标 = 「**感觉得到在下雨，但几乎看不到独立粒子**」。 */
    /* 🔴🔴 v45（用户选「甲」）：方向**翻回「↙ 向左下」**（vx 取负）。
       ⚠️ 这次之所以能翻对，是因为 v44 修掉了 buildStreaks 里"头端 x 与 slope 反号"的老 bug ——
          修之前改 vx 符号根本改不动方向。长度保持 v44 收短后的值（短促水线，不是拖影）。 */
    drizzle: { vy: [260, 420],  vx: -78,  len: [8, 16] },
    rain:    { vy: [680, 980],  vx: -250, len: [12, 26] },
    thunder: { vy: [820, 1150], vx: -300, len: [14, 30] },
    snow:    { vy: [42, 126],   drift: [7, 20] }
  };

  var WX_KEY  = '9758264962c64aa3a8c44316260210';
  var WX_BASE = 'https://api.weatherapi.com/v1/forecast.json';
  /* 🔴 v48（用户要求「改为 30 分钟请求一次」）：15 → 30 分钟。
     这一个常量**同时是两件事的窗口**：① localStorage 客户端缓存的有效期
     ② CDN cache-buster 的 bucket（见 fetchLive）⇒ 每 30 分钟窗口全球只回源 1 次。
     ⇒ 用量直接减半（约 48 次/天，月 ≈ 1,450 次）。代价：天气最多"旧" 30 分钟 —— 可接受。 */
  var WX_TTL  = 30 * 60 * 1000;
  var WX_CK   = 'hdxWx.v1';

  /* 兜底坐标：仅用于 __hdWx.DEMO 的向后兼容展示 */
  var DEMO = { lat: 23.11, lon: 114.42, name: 'Huizhou' };

  var st = {
    kind: 'auto', phase: 'auto',
    temp: null, code: null, isDay: null, cloud: null, localDate: null,
    sun: null, place: null, localMin: null, at: 0,
    src: '—', effKind: 'clear', effPhase: 'day',
    count: 0, cloudN: 0, fogN: 0, sunOn: false, boltOn: false, moonOn: false, starN: 0, moonMode: 'off',
    scale: 0, buf: '—', obsCss: 0, frames: 0
  };

  var subs = [];
  var tintEls = {}, clouds = [], fogs = [], cv = null, cx = null, flash = null, theSun = null, theBolt = null, theRays = null;
  var theMoon = null, starsWrap = null, stars = [];
  var parts = [], raf = 0, lastT = 0;
  var scale = 0.5;
  var kindNow = 'clear', phaseNow = 'day';
  var obsSum = 0, obsT = 0;

  function emit() {
    st.effKind = kindNow; st.effPhase = phaseNow; st.count = parts.length;
    st.cloudN = clouds.filter(function (c) { return c.classList.contains('is-on'); }).length;
    st.fogN = fogs.filter(function (c) { return c.classList.contains('is-on'); }).length;
    st.sunOn = !!(theSun && theSun.classList.contains('is-on'));
    st.moonOn = !!(theMoon && theMoon.classList.contains('is-on'));
    st.moonMode = theMoon ? (theMoon.getAttribute('data-moon') || 'off') : 'off';
    st.starN = (starsWrap && starsWrap.classList.contains('is-on')) ? stars.length : 0;
    st.boltOn = !!(theBolt && theBolt.classList.contains('is-on'));
    st.scale = scale; st.buf = cv ? (cv.width + '×' + cv.height) : '—';
    for (var i = 0; i < subs.length; i++) { try { subs[i](st); } catch (e) {} }
    try { W.dispatchEvent(new CustomEvent('hdwx', { detail: st })); } catch (e2) {}
  }

  /* ------------------------------------------------------------------ 建层 */
  function mkBoltPath(d, op, blurId) {
    var p = D.createElementNS(NS, 'path');
    p.setAttribute('d', d);
    p.setAttribute('fill', 'rgba(255,255,250,' + op + ')');
    if (blurId) p.setAttribute('filter', 'url(#' + blurId + ')');
    return p;
  }

  function buildBoltSvg() {
    var svg = D.createElementNS(NS, 'svg');
    svg.setAttribute('viewBox', '0 0 1000 600');
    svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
    var defs = D.createElementNS(NS, 'defs');
    var f = D.createElementNS(NS, 'filter');
    f.setAttribute('id', 'pfWxGlow');
    f.setAttribute('x', '-30%'); f.setAttribute('y', '-30%');
    f.setAttribute('width', '160%'); f.setAttribute('height', '160%');
    var g = D.createElementNS(NS, 'feGaussianBlur');
    g.setAttribute('stdDeviation', '7');
    f.appendChild(g); defs.appendChild(f); svg.appendChild(defs);

    var MAIN = 'M520 -12 L468 168 L522 180 L428 362 L492 374 L382 574 L472 400 L414 386 L502 208 L448 196 Z';
    var SIDE = 'M742 -12 L712 104 L748 112 L690 226 L730 234 L666 366 L718 254 L684 246 L736 130 L706 122 Z';
    /* 先画发光层（模糊、低不透明），再画实心主体 —— 顺序决定叠放 */
    svg.appendChild(mkBoltPath(MAIN, .58, 'pfWxGlow'));
    svg.appendChild(mkBoltPath(SIDE, .46, 'pfWxGlow'));
    svg.appendChild(mkBoltPath(MAIN, 1));
    svg.appendChild(mkBoltPath(SIDE, .92));
    return svg;
  }

  /* 星点布点：**固定不随机** —— 每次刷新位置一致，观感才稳定。
     [x%, y%, 直径px, 闪烁周期s, 相位偏移s] */
  var STARS = [
    [7, 13, 2.4, 3.6, -0.2], [16, 27, 1.7, 4.4, -1.3], [25, 8, 2.1, 3.9, -2.6],
    [34, 20, 1.5, 5.1, -0.8], [43, 32, 1.9, 4.2, -3.1], [52, 10, 2.6, 3.4, -1.9],
    [60, 23, 1.6, 4.8, -2.2], [68, 14, 2.2, 3.7, -0.5], [76, 29, 1.7, 4.6, -3.4],
    [84, 18, 2.3, 4.0, -1.1], [12, 42, 1.6, 4.9, -1.6], [29, 48, 2.0, 3.8, -2.4],
    [46, 40, 1.7, 4.5, -0.9], [63, 45, 1.9, 4.1, -3.2], [80, 38, 1.5, 5.0, -1.4],
    [22, 59, 1.8, 4.3, -2.7], [57, 56, 1.6, 4.7, -0.6], [41, 62, 1.5, 5.2, -3.6]
  ];

  /* 月亮 = SVG 月牙 + 一圈柔光（光晕画在 SVG 内，不用 CSS filter，避免过渡类重绘） */
  function buildMoonSvg() {
    var svg = D.createElementNS(NS, 'svg');
    svg.setAttribute('viewBox', '0 0 200 200');
    var defs = D.createElementNS(NS, 'defs');

    /* 🔴 这里**不再建 filter**：柔光改由外层元素的 CSS 径向渐变承担。
       （原来的 circle+feGaussianBlur 超出 viewBox 会被裁成方角 —— 见 CSS 里的说明。） */

    /* 用 mask 把主圆"咬掉"一块 ⇒ 月牙 */
    var mk = D.createElementNS(NS, 'mask');
    mk.setAttribute('id', 'pfWxMoonCut');
    var rc = D.createElementNS(NS, 'rect');
    rc.setAttribute('width', '200'); rc.setAttribute('height', '200'); rc.setAttribute('fill', '#fff');
    mk.appendChild(rc);
    var cut = D.createElementNS(NS, 'circle');
    cut.setAttribute('cx', '126'); cut.setAttribute('cy', '70'); cut.setAttribute('r', '70');
    cut.setAttribute('fill', '#000');
    mk.appendChild(cut);
    defs.appendChild(mk);

    /* 月面明暗 + 环形山：平涂的月亮看起来像"贴纸"（用户：「月亮效果不好」） */
    var sh = D.createElementNS(NS, 'radialGradient');
    sh.setAttribute('id', 'pfWxMoonShade');
    sh.setAttribute('cx', '36%'); sh.setAttribute('cy', '30%'); sh.setAttribute('r', '82%');
    var stops = [['0%', '#FFFDF4'], ['58%', '#EFEAD6'], ['100%', '#C6BFA3']];
    for (var si = 0; si < stops.length; si++) {
      var sp2 = D.createElementNS(NS, 'stop');
      sp2.setAttribute('offset', stops[si][0]);
      sp2.setAttribute('stop-color', stops[si][1]);
      sh.appendChild(sp2);
    }
    defs.appendChild(sh);

    /* 2026-10-16：**真扫光** —— 一条斜向光带，用**同一个月牙 mask**（pfWxMoonCut）遮罩
       ⇒ 光只会出现在月牙本体上（不会溢到缺口那侧、也不会露出方角）。
       光带本身是「左暗-中亮-右暗」的横向渐变，配合 CSS 的 translateX 动画 ⇒ 看起来就是光在扫过月面。 */
    /* 2026-10-16（用户「改回光带式」）：回到斜向线性光带。
       已修好的继续保留：遮罩固定 <g>、月牙形状 mask、移动范围收窄（CSS ±22%）
       ⇒ 光带只在月牙内扫，不再跑到左侧空区。 */
    var swGrad = D.createElementNS(NS, 'linearGradient');
    swGrad.setAttribute('id', 'pfWxMoonSweep');
    swGrad.setAttribute('x1', '0'); swGrad.setAttribute('y1', '0');
    swGrad.setAttribute('x2', '1'); swGrad.setAttribute('y2', '0.42');
    var swStops = [['0%', 'rgba(255,255,255,0)'], ['42%', 'rgba(255,255,255,0)'],
                   ['50%', 'rgba(255,255,255,.92)'], ['58%', 'rgba(228,238,255,.42)'],
                   ['100%', 'rgba(255,255,255,0)']];
    for (var wi = 0; wi < swStops.length; wi++) {
      var ws = D.createElementNS(NS, 'stop');
      ws.setAttribute('offset', swStops[wi][0]);
      ws.setAttribute('stop-color', swStops[wi][1]);
      swGrad.appendChild(ws);
    }
    defs.appendChild(swGrad);

    /* 🔴 2026-10-16：扫光专用 mask —— **真正的月牙形状**（白圆 − 黑圆）。
       不能复用 pfWxMoonCut：那个是「200×200 白方框 − 黑圆」，必须配合月牙本体的 <circle> 才成月牙；
       单独套在扫光层上会裁出「大方框缺个圆角」⇒ 就是用户截图里的方块/菱形伪影。 */
    var swMask = D.createElementNS(NS, 'mask');
    swMask.setAttribute('id', 'pfWxMoonSweepMask');
    /* ① 半径各收 3px（74→71、70→67）：遮罩向内缩，边缘不再贴着月牙轮廓
       ⇒ 消灭「扫光移动时边缘 1px 忽隐忽现」的漏光闪烁。 */
    var swBody = D.createElementNS(NS, 'circle');
    swBody.setAttribute('cx', '94'); swBody.setAttribute('cy', '102'); swBody.setAttribute('r', '71');
    swBody.setAttribute('fill', '#fff');
    swMask.appendChild(swBody);
    var swCut = D.createElementNS(NS, 'circle');
    swCut.setAttribute('cx', '126'); swCut.setAttribute('cy', '70'); swCut.setAttribute('r', '67');
    swCut.setAttribute('fill', '#000');
    swMask.appendChild(swCut);
    defs.appendChild(swMask);

    svg.appendChild(defs);

    /* 月牙 + 环形山**同挂一个 mask** ⇒ 环形山只会出现在月牙里，不会溢到缺口那侧 */
    var grp = D.createElementNS(NS, 'g');
    grp.setAttribute('mask', 'url(#pfWxMoonCut)');
    var disc = D.createElementNS(NS, 'circle');
    disc.setAttribute('cx', '94'); disc.setAttribute('cy', '102'); disc.setAttribute('r', '74');
    disc.setAttribute('fill', 'url(#pfWxMoonShade)');
    grp.appendChild(disc);
    var CRATERS = [[74, 78, 13, .40], [108, 118, 9, .34], [60, 120, 6, .30], [116, 74, 6.5, .28]];
    for (var ci = 0; ci < CRATERS.length; ci++) {
      var cr = CRATERS[ci], cc = D.createElementNS(NS, 'circle');
      cc.setAttribute('cx', String(cr[0])); cc.setAttribute('cy', String(cr[1]));
      cc.setAttribute('r', String(cr[2]));
      cc.setAttribute('fill', 'rgba(196, 189, 162, ' + cr[3] + ')');
      grp.appendChild(cc);
    }
    svg.appendChild(grp);

    /* 扫光层：压在月牙之上，靠 CSS 动画横向扫过（class 见 pf-wx.css 的 .pf-wx-moonsweep） */
    var sweep = D.createElementNS(NS, 'rect');
    sweep.setAttribute('class', 'pf-wx-moonsweep');
    sweep.setAttribute('x', '0'); sweep.setAttribute('y', '0');
    sweep.setAttribute('width', '200'); sweep.setAttribute('height', '200');
    sweep.setAttribute('fill', 'url(#pfWxMoonSweep)');
    /* 🔴 2026-10-16 修 bug：mask 必须挂在**固定不动的 <g>** 上，不能挂在会被 translateX 移动的 rect 上
       —— 否则 mask 跟着 rect 一起平移，月牙裁剪跑偏，rect 的方形本体就露出来了
       （用户截图：月牙周围出现方块/菱形伪影）。 */
    var sweepWrap = D.createElementNS(NS, 'g');
    sweepWrap.setAttribute('mask', 'url(#pfWxMoonSweepMask)');   /* 月牙形状（不是方框−圆） */
    sweepWrap.appendChild(sweep);
    svg.appendChild(sweepWrap);

    return svg;
  }

  function build() {
    var i, weatherFrag = D.createDocumentFragment(), skyFrag = D.createDocumentFragment();

    /* ---- 天气实体（z 3 / 粒子 z -2，插在 .site-bg 之后）---- */
    /* 🔴🔴 v15 手机适配（用户：「手机版，全部看不到啊」）——两个几何根因：
       ① **云宽是固定 px**（620/470/800/540），而手机视口才 390px ⇒ 视口里只剩云的边角/空隙，
          看着就是"什么都没有"。⇒ 手机上限死**视口的 86%**，保证一整朵云落在屏内。
       ② **手机顶栏占视口约 22%**（390×844 实测 187px；平板 1440 只占 12%）⇒ 云 top 8~23% 有一多半
          被导航栏压住。⇒ 手机整体下移 14 个百分点躲开。
          ⚠️ 移动端不能只看"百分比"：同一个 8% 在 844 高和 1194 高上，被顶栏吃掉的比例完全不同。 */
    var vw = innerWidth, narrow = vw < 600;
    for (i = 0; i < CLOUDS.length; i++) {
      var c = CLOUDS[i], el = D.createElement('div');
      el.className = 'pf-wx-cloud';
      el.setAttribute('aria-hidden', 'true');
      var cw = Math.min(c.w, Math.round(vw * 0.86));
      var ch = Math.round(c.h * (cw / c.w));
      el.style.width = cw + 'px';
      el.style.height = ch + 'px';
      el.style.top = (narrow ? c.top + 14 : c.top) + '%';
      el.style.right = 'auto';
      el.style.animationDuration = c.dur + 's';
      el.style.animationDelay = c.delay + 's';
      clouds.push(el); weatherFrag.appendChild(el);
    }
    for (i = 0; i < FOGBANDS.length; i++) {
      var b = FOGBANDS[i], fb = D.createElement('div');
      fb.className = 'pf-wx-fogband';
      fb.setAttribute('aria-hidden', 'true');
      fb.style.height = b.h + 'px';
      /* 🔴 v15：手机顶栏占 22%（平板 12%）⇒ 雾整体下移 6 个百分点（只下移一点点：雾被顶栏压一点无所谓，
         主要是保证**第一条完整露在导航栏下方**）。 */
      fb.style.top = (narrow ? b.top + 6 : b.top) + '%';
      /* x = 水平错开（%）。CSS 默认是 left/right 各 -14%（铺满整屏）；
         给了 x 就改成显式 left + width，让三条各自偏一点 ⇒ 不再是一条条横贯全屏的带 */
      if (b.x !== undefined) {
        fb.style.left = b.x + '%';
        fb.style.right = 'auto';
        fb.style.width = '92%';
      }
      fb.style.animationDuration = b.dur + 's';
      fb.style.animationDelay = (-b.dur / 2) + 's';
      fogs.push(fb); weatherFrag.appendChild(fb);
    }
    cv = D.createElement('canvas');
    cv.className = 'pf-wx-canvas';
    cv.setAttribute('aria-hidden', 'true');
    weatherFrag.appendChild(cv);

    /* ---- 天色 + 光源（z -1，插在 .site-bg-overlay 之后）---- */
    for (i = 0; i < PHASES.length; i++) {
      var t = D.createElement('div');
      t.className = 'pf-wx-tint';
      t.setAttribute('data-k', PHASES[i]);
      t.setAttribute('aria-hidden', 'true');
      tintEls[PHASES[i]] = t; skyFrag.appendChild(t);
    }
    theSun = D.createElement('div');
    theSun.className = 'pf-wx-sun';
    theSun.setAttribute('aria-hidden', 'true');
    skyFrag.appendChild(theSun);
    /* 🔴 v42 晴天专用：日光斜射 + 光斑层（对标大屏天气可视化的做法 —— 线性渐变模拟阳光、
       几个大半径柔圆光斑模拟"照在玻璃上的光"）。单独一层，比塞进太阳元素好调。
       ⚠️ 只在晴天 + 非夜晚显示（夜里还晒太阳会很假，CSS 与 JS 双重把关）。 */
    theRays = D.createElement('div');
    theRays.className = 'pf-wx-sunrays';
    theRays.setAttribute('aria-hidden', 'true');
    skyFrag.appendChild(theRays);

    /* 月亮 + 星点：**只属于「晴 + 夜晚」**（v4 之前这一档页面上是空的） */
    theMoon = D.createElement('div');
    theMoon.className = 'pf-wx-moon';
    theMoon.setAttribute('aria-hidden', 'true');
    theMoon.appendChild(buildMoonSvg());
    skyFrag.appendChild(theMoon);

    starsWrap = D.createElement('div');
    starsWrap.className = 'pf-wx-stars';
    starsWrap.setAttribute('aria-hidden', 'true');
    for (i = 0; i < STARS.length; i++) {
      var sp = STARS[i], sEl = D.createElement('span');
      sEl.className = 'pf-wx-star';
      sEl.style.left = sp[0] + '%';
      sEl.style.top = sp[1] + '%';
      sEl.style.width = sp[2] + 'px';
      sEl.style.height = sp[2] + 'px';
      sEl.style.animationDuration = sp[3] + 's';
      sEl.style.animationDelay = sp[4] + 's';
      stars.push(sEl); starsWrap.appendChild(sEl);
    }
    skyFrag.appendChild(starsWrap);

    theBolt = D.createElement('div');
    theBolt.className = 'pf-wx-bolt';
    theBolt.setAttribute('aria-hidden', 'true');
    theBolt.appendChild(buildBoltSvg());
    skyFrag.appendChild(theBolt);

    flash = D.createElement('div');
    flash.className = 'pf-wx-flash';
    flash.setAttribute('aria-hidden', 'true');
    skyFrag.appendChild(flash);

    /* 🔴🔴 v16（用户：「效果，放在背景图上，在其他层之下」）—— 层级回到"内容之下"，
       同时**修掉一处一直存在的旧毛病**：天气实体原来插在 `.site-bg` 之后，
       于是又被 `.site-bg-overlay`（rgba(253,246,250,.42) 的浅色罩）**冲淡 42%** ——
       这才是"底层时云/雾很淡"的主因之一（以前一直当纯颜色问题在调）。
       现在把「天气实体 + 天色罩」两个 fragment **合并后整体插到 `.site-bg-overlay` 之后**：
         背景图(-2) → 浅色罩(-1) → 天气实体(-1，DOM 靠后 ⇒ 压过浅色罩) → 天色罩(-1) → 页面内容(auto)
       ⇒ 不被浅色罩冲淡、仍被天色染色、而且**全部落在页面内容之下**（= 用户要的"在其他层之下"）。
       ⚠️ 代价（已告知用户）：窄屏 hero 是一张几乎占满宽的大图 ⇒ 云/雾会被它盖住。 */
    weatherFrag.appendChild(skyFrag);   /* 实体在前、天色在后 ⇒ 天色压在实体之上给它染色 */
    var ref = D.querySelector('.site-bg-overlay') || D.querySelector('.site-bg');
    /* 🔴 v29（用户：「往下滚的时候，这三个已经看不到」）——
       天气层要从「钉在屏幕（fixed）」改成「**跟着页面一起滚走**（absolute）」。
       做法：外面包一层 `.pf-wx-layer`，它 **height: 100vh** ⇒ 所有子元素原本按视口算的
       `%` / `vw` / `vh` 数值**一个都不用改**，只是坐标系从"视口"变成"这一层"，
       而这一层挂在文档流顶部 ⇒ **随页面滚动**。 */
    var layer = D.createElement('div');
    layer.className = 'pf-wx-layer';
    layer.setAttribute('aria-hidden', 'true');
    layer.appendChild(weatherFrag);
    if (ref && ref.parentNode) ref.parentNode.insertBefore(layer, ref.nextSibling);
    else D.body.insertBefore(layer, D.body.firstChild);

    cx = cv.getContext ? cv.getContext('2d') : null;
  }

  /* ---------------------------------------------------------------- 相位 */
  /* 🔴 WeatherAPI 的日出日落是 **12 小时制**（"06:14 AM" / "06:09 PM"），
     而 location.localtime 是 24 小时制 —— hm() 必须两种都吃，否则相位全错。 */
  function hm(s) {
    var m = /^(\d{1,2}):(\d{2})(?::\d{2})?\s*([AaPp][Mm])?/.exec(String(s || '').trim());
    if (!m) return null;
    var v = ((+m[1]) % 12) + (+m[2]) / 60;
    if (m[3] && m[3].toUpperCase() === 'PM') v += 12;
    return (v >= 0 && v < 24) ? v : null;
  }

  /* 「甲」：天数相位以**该地当地时间**为准（不是设备钟点）⇒ VPN / 出差下才自洽。
     存的是 localMin + 取数时刻，所以页面停着不动时它也会"走时"。 */
  function localHourNow() {
    if (st.localMin === null || st.localMin === undefined) return null;
    var mins = st.localMin + (Date.now() - st.at) / 60000;
    return (((mins % 1440) + 1440) % 1440) / 60;
  }

  function phaseFromClock(sun) {
    var h = localHourNow();
    if (h === null) { var d = new Date(); h = d.getHours() + d.getMinutes() / 60; }
    var rise = 6.2, set = 18.2;
    if (sun) {
      var r = hm(sun.sunrise), s2 = hm(sun.sunset);
      if (r !== null && s2 !== null && s2 > r + 2) { rise = r; set = s2; }
    }
    if (h >= rise - 0.85 && h < rise + 0.55) return 'dawn';
    if (h >= rise + 0.55 && h < set - 0.80) return 'day';
    if (h >= set - 0.80 && h < set) return 'golden';
    if (h >= set && h < set + 0.85) return 'dusk';
    return 'night';
  }

  function syncSun() {
    if (!theSun) return;
    var p = PLAN[kindNow] || PLAN.clear;
    var isNight = (phaseNow === 'night');
    /* 2026-10-20：太阳/光晕是**静态光源**，不该被 REDUCE 隐藏 —— 与月亮同口径
       （月亮注释已写明「不随 REDUCE 隐藏，只由 CSS 停呼吸动画」）。
       原实现多了 !REDUCE ⇒ 系统开「减少动态效果」时晴天**没有任何东西**（用户实测：电脑端看不到天气层）。
       动画停止已由 pf-wx.css 的 @media (prefers-reduced-motion) 负责。 */
    theSun.classList.toggle('is-on', !!p.sun && !isNight);
    if (theRays) theRays.classList.toggle('is-on', !!p.sun && !isNight);
    /* 🔴 夜里的月亮按天气分三态（v9；原来是"只有晴夜才有月亮"）：
         clear  → full   ：月亮全显 + 星点
         cloudy → veiled ：月亮**被云半遮**（CSS 的 .pf-wx-moon::after 云形遮片），不给星点
         其余   → off    ：浓雾 / 厚云降水，看不见月亮（更真实）
       月亮不随 REDUCE 隐藏（它本来就是静态的），只由 CSS 停掉呼吸动画。 */
    var moonMode = 'off';
    if (isNight) {
      if (kindNow === 'clear') moonMode = 'full';
      else if (kindNow === 'cloudy') moonMode = 'veiled';
    }
    if (theMoon) {
      theMoon.classList.toggle('is-on', moonMode !== 'off');
      theMoon.setAttribute('data-moon', moonMode);
    }
    if (starsWrap) starsWrap.classList.toggle('is-on', moonMode === 'full');
  }

  function applyPhase(p) {
    phaseNow = p;
    for (var i = 0; i < PHASES.length; i++) {
      var el = tintEls[PHASES[i]];
      if (el) el.classList.toggle('is-on', PHASES[i] === p);
    }
    root.setAttribute('data-hdx-phase', p);
    syncSun();          /* 夜里没有太阳 */
  }

  /* ---------------------------------------------------------------- 天气 */
  /* 🔴 WeatherAPI 用**自家天气码**（不是 WMO）⇒ 整套换掉，别沿用旧映射。
     码表来源：https://www.weatherapi.com/docs/weather_conditions.json */
  var WA_KIND = {};
  (function () {
    function put(kind, list) { for (var i = 0; i < list.length; i++) WA_KIND[list[i]] = kind; }
    put('clear',   [1000]);
    put('cloudy',  [1003, 1006, 1009]);
    put('cloudy',  [1030, 1135, 1147]);   /* v24：雾码归多云 ⇒ 真下雾也不显示雾 */
    put('drizzle', [1072, 1150, 1153, 1168, 1171]);
    put('rain',    [1063, 1180, 1183, 1186, 1189, 1192, 1195, 1198, 1201, 1240, 1243, 1246]);
    put('snow',    [1066, 1069, 1114, 1117, 1204, 1207, 1210, 1213, 1216, 1219, 1222, 1225,
                    1237, 1249, 1252, 1255, 1258, 1261, 1264]);
    put('thunder', [1087, 1273, 1276, 1279, 1282]);
  })();

  function codeToKind(c) {
    c = +c;
    if (WA_KIND[c]) return WA_KIND[c];
    /* 官方若新增码：给个中性兜底（比乱猜成雨/雪安全） */
    return 'cloudy';
  }

  function countFor(k) {
    /* 🔴 v41（对标 Apple Weather / atmos-fx 实测）：大平台雨滴是**数百~数千**，
       我们原来 1440×900 只有 54 颗 ⇒ 稀疏得像"几根线"而不是"在下雨"。
       密度基准从 /24000 提到 /11000（约 2.2 倍），并把雨的 cap 从 120 提到 260。 */
    /* 🔴 v43（用户「密度有问题」）：实测 118 颗 = 每 10,983px² 一颗、覆盖 1.70%，
       而且**完全均匀**（像撒芝麻）。真雨是疏密不均的 ⇒ 总数降到约 3/4，靠下面的
       "成团"布点造疏密，而不是靠堆数量。 */
    var base = Math.round((W.innerWidth * W.innerHeight) / 15000);
    /* 🔴 毛毛雨要比雨**更密**（"毛"的体感 = 细碎而密集）。原来 drizzle 与 rain 的 base
       完全相同（1440×900 都是 54 个）⇒ 密度上也没区别，这是"没有自己样子"的另一半原因。 */
    if (k === 'drizzle') return Math.max(46, Math.min(160, Math.round(base * 1.5)));
    var cap = k === 'snow' ? 130 : 190;
    return Math.max(34, Math.min(cap, base));
  }

  /* 🔴 缓冲区尺寸只认元素自身盒子（不是 innerHeight）—— "手机上掉得快"的根因修复 */
  function resize() {
    if (!cv) return;
    var r = cv.getBoundingClientRect();
    var w = Math.max(200, Math.round(r.width || W.innerWidth));
    var h = Math.max(200, Math.round(r.height || W.innerHeight));
    scale = Math.min(0.5, 900 / w);
    cv.width = Math.round(w * scale);
    cv.height = Math.round(h * scale);
  }

  function rnd(a) { return a[0] + Math.random() * (a[1] - a[0]); }

  function seed(init) {
    var w = cv.width, h = cv.height, K = scale;
    if (kindNow === 'snow') {
      var s = SPEED.snow;
      return {
        x: Math.random() * w, y: init ? Math.random() * h : -6,
        r: (0.9 + Math.random() * 1.7) * Math.max(1, K * 2),
        vy: rnd(s.vy) * K, dr: rnd(s.drift) * K,
        ph: Math.random() * 6.283, a: 0.42 + Math.random() * 0.45
      };
    }
    var sp = SPEED[kindNow] || SPEED.rain;
    /* 🔴 v41 **真深度分层**（对标 Apple：depth 同时影响速度/长度/透明度，形成视差）：
       原来是「长度随机 × 3 档轮换」的假分层。现在给每颗一个 dep(0=远,1=近)：
         远 ⇒ 慢(×0.55)、短(×0.6)、淡(×0.5)
         近 ⇒ 快(×1.35)、长(×1.25)、亮(×1.0)
       这样远近雨滴各按自己的速度跑，层次是**运动**出来的，不是贴上去的。 */
    var dep = Math.random(), K2 = 0.55 + dep * 0.8;
    return {
      x: Math.random() * w, y: init ? Math.random() * h : -18,
      len: rnd(sp.len) * Math.max(1, K * 2) * (0.6 + dep * 0.65),
      vy: rnd(sp.vy) * K * K2, vx: sp.vx * K * K2,
      dep: dep, tier: dep < 0.34 ? 0 : (dep < 0.7 ? 1 : 2),
      /* 毛毛雨原来 0.18~0.33 在浅色站上几乎看不见；而且它是**断续短划**，
         每颗的墨量本就比连续线少 ⇒ 单笔不透明度要更高才等效。 */
      a: (kindNow === 'drizzle' ? (0.30 + Math.random() * 0.22) : (0.24 + Math.random() * 0.30)) * (0.5 + dep * 0.5)
    };
  }

  function seedParts() {
    resize();
    var n = countFor(kindNow);
    parts = new Array(n);
    for (var i = 0; i < n; i++) parts[i] = seed(true);
    obsSum = 0; obsT = 0; st.obsCss = 0;
  }

  function ink(a) {
    return (phaseNow === 'night' || phaseNow === 'dusk')
      ? 'rgba(232,236,255,' + a.toFixed(3) + ')'
      : 'rgba(78,86,118,' + (a * 0.95).toFixed(3) + ')';
  }

  /* ==================== v41 溅落（对标 Apple Weather 的核心亮点）====================
     调研结论：Apple 天气那种"高级感"**主要不来自雨滴本身，而来自雨打在界面元素上溅起来**。
     做法：测出页面上卡片的顶边（视口坐标 × scale = 缓冲坐标），雨滴越过它时生成 3~5 颗小水花
     （斜向外抛 + 重力 + 淡出）。这样"背景贴图"就变成了"真实天气"。
     ⚠️ 障碍物最多记 22 个、每 1.2s 重测一次（getBoundingClientRect 有强制布局开销，不能每帧调）。 */
  var obs = [], spl = [], obsAt = 0;
  function measureObs() {
    obs = [];
    var els = D.querySelectorAll('article, .card, section > div, [class*="card"]');
    for (var i = 0; i < els.length && obs.length < 22; i++) {
      var b = els[i].getBoundingClientRect();
      if (b.width < 90 || b.height < 50 || b.top < 8 || b.bottom > W.innerHeight - 4) continue;
      obs.push({ x: b.left * scale, y: b.top * scale, w: b.width * scale });
    }
    obsAt = Date.now();
  }
  function spawnSplash(x, y, n) {
    for (var i = 0; i < n; i++) {
      spl.push({ x: x, y: y, vx: (Math.random() - 0.5) * 46, vy: -Math.random() * 58 - 12,
                 life: 1, r: Math.max(0.7, (0.7 + Math.random() * 1.2) * scale * 2) });
    }
    if (spl.length > 260) spl.splice(0, spl.length - 260);
  }

  function step(dt) {
    var i, p, w = cv.width, h = cv.height, moved = 0;
    if (kindNow === 'snow') {
      for (i = 0; i < parts.length; i++) {
        p = parts[i];
        p.ph += 0.85 * dt;
        var dy = p.vy * dt;
        p.y += dy;
        p.x += Math.sin(p.ph) * p.dr * dt - 0.9 * dt;
        moved += Math.abs(dy);
        if (p.y > h + 6) { parts[i] = seed(false); }
        else if (p.x < -8) p.x = w + 6; else if (p.x > w + 8) p.x = -6;
      }
      return moved;
    }
    for (i = 0; i < parts.length; i++) {
      p = parts[i];
      var dy2 = p.vy * dt;
      p.y += dy2; p.x += p.vx * dt;
      moved += Math.abs(dy2);
      /* v41：越过某张卡片的顶边 ⇒ 溅落，并把这颗雨滴回收（"打掉了"） */
      if (obs.length && p.y > 0) {
        for (var oi = 0; oi < obs.length; oi++) {
          var o = obs[oi];
          if (p.y >= o.y && p.y <= o.y + 16 && p.x >= o.x && p.x <= o.x + o.w) {
            spawnSplash(p.x, o.y, 2 + ((Math.random() * 3) | 0));
            parts[i] = seed(false);
            break;
          }
        }
      }
      if (p.y - p.len > h) { parts[i] = seed(false); }
      else if (p.x < -30 * Math.max(1, scale * 2)) { p.x = w + 20; }
    }
    return moved;
  }

  /* v41：溅落粒子的推进（重力 + 淡出）。放在 loop 里调用 ⇒ 雨/雪/雷暴共用，不必各写一遍。 */
  function stepSplash(dt) {
    if (Date.now() - obsAt > 1200) measureObs();
    for (var i = spl.length - 1; i >= 0; i--) {
      var s = spl[i];
      s.vy += 260 * dt;
      s.x += s.vx * dt;
      s.y += s.vy * dt;
      s.life -= 1.9 * dt;
      if (s.life <= 0) spl.splice(i, 1);
    }
  }

  function draw() {
    if (!cx) return;
    var i, p;
    cx.clearRect(0, 0, cv.width, cv.height);
    if (kindNow === 'snow') {
      for (i = 0; i < parts.length; i++) {
        p = parts[i];
        cx.beginPath();
        cx.arc(p.x, p.y, p.r, 0, 6.2832);
        cx.fillStyle = 'rgba(255,255,255,' + p.a.toFixed(3) + ')';
        cx.fill();
      }
      return;
    }
    /* 🔴🔴 v35（用户选「乙」）雨改成**游戏雨痕**：头亮尾淡的模糊光带，带速度拖尾。
       ☠️ 关键性能点：**渐变绝不每帧现建** —— 几百个粒子 × 60fps 调用 createLinearGradient
          会直接把帧率打崩。做法是**预烘焙 3 档离屏图**（短/中/长，景深），
          而且把**倾斜角逐个烘焙进图里** ⇒ 每帧只剩 `drawImage`（零 transform、零渐变创建）。
       ⚠️ 离屏图的"头"要落在粒子的 (p.x,p.y) 上（粒子的坐标本来就是头部）⇒ ox/oy 取图内头端点。 */
    /* v39：相位（= 雨点墨色）变化时也要重建，否则白天切夜晚还留着白天那套灰蓝墨。 */
    if (!STREAKS[kindNow] || STREAKS.sc !== scale || STREAKS.ph !== st.effPhase) buildStreaks(kindNow);
    var _S = STREAKS[kindNow];
    for (i = 0; i < parts.length; i++) {
      p = parts[i];
      var _t = _S[i % _S.length];
      cx.globalAlpha = p.a;
      cx.drawImage(_t.img, p.x - _t.ox, p.y - _t.oy, _t.w, _t.h);
    }
    cx.globalAlpha = 1;
    /* v41：溅落水花（画在最上层，压过雨丝） */
    if (spl.length) {
      var mc = (st.effPhase === 'night' || st.effPhase === 'dusk') ? '232,236,255' : '78,86,118';
      for (i = 0; i < spl.length; i++) {
        var spl_ = spl[i];
        cx.beginPath();
        cx.arc(spl_.x, spl_.y, spl_.r, 0, 6.2832);
        cx.fillStyle = 'rgba(' + mc + ',' + (spl_.life * 0.6).toFixed(3) + ')';
        cx.fill();
      }
    }
  }

  /** 预烘焙雨痕（v35）：每档 = 一张斜向的"头亮尾淡"离屏图，带柔和外发光。 */
  var STREAKS = { sc: 0 };
  function buildStreaks(kind) {
    var sp = SPEED[kind] || SPEED.rain;
    var slope0 = sp.vx / ((sp.vy[0] + sp.vy[1]) / 2);     /* <0：向左下偏 */
    var sc = Math.max(1, scale * 2);
    var driz = (kind === 'drizzle');
    var out = [];
    for (var k = 0; k < 3; k++) {
      /* 🔴🔴 v43（用户「角度也错了」）—— 真凶不是方向，而是**所有雨滴角度完全一致**：
         烘焙成同一张图 ⇒ 看上去是一排印刷平行线。真雨同风向但每滴有 ±3° 的自然差异。
         ⇒ **每一档用不同斜率**（三个长度档配三个角度），混起来就有"雨帘"的层次感。
         ⚠️ 注意 `slope` 参与图宽计算，所以必须在算 W2 之前定好。 */
      var slope = slope0 * (0.80 + k * 0.22);
      var len = (sp.len[0] + (sp.len[1] - sp.len[0]) * (k / 2)) * sc;
      /* 🔴🔴 v38 真因修正：诊断实测"完全看不见"时最大像素 alpha 只有 **14/255(5.5%)**，
         问题**不在透明度**，而在**线太细** —— canvas 是半分辨率缓冲（实测 720×450，scale=0.5），
         线宽 1.4 缓冲px 只有 0.7 屏幕 px，再经抗锯齿摊到几个像素上 ⇒ 每像素 alpha 跌到 5%。
         ⇒ 加粗到 2.4（毛毛雨 1.6），并把光晕进一步压低（光晕越强、能量越摊薄）。 */
      /* 🔴 v46（用户「雨点太粗了」）：3.6 → **2.2**（毛毛雨 2.4 → 1.5）。
         只减细、不降亮度 —— 目标是"细而有实感的水丝"。
         之所以现在敢减细：细线原先看不见的两个原因（灰蓝脏色 + 光晕摊薄）都已在 v39/v40 修掉。 */
      var wid = (driz ? 1.5 : 2.2) * sc * (0.72 + k * 0.28);
      var pad = wid * 3.2;                                 /* 给 shadowBlur 留边，否则光晕被裁成直角 */
      var W2 = Math.ceil(Math.abs(slope) * len + pad * 2), H2 = Math.ceil(len + pad * 2);
      var cvs = D.createElement('canvas');
      cvs.width = W2; cvs.height = H2;
      var g = cvs.getContext('2d');
      /* ☠️ 原来写的是 `x1 = x0 - slope * len` —— **头端 x 与 slope 反号**，
         所以把 vx 改成正值（= 粒子向右下走）之后，画出来的头仍然偏左，
         实测"向右下"改不动（量到 -12px 还是左下）。⇒ 按**运动方向**重写：
         头端 = 雨滴落下的那一端，必须落在 vx 的一侧。sx>0（右下）时头在右、尾在左；反之对调。 */
      var sx = slope >= 0 ? 1 : -1;
      var xTail = sx > 0 ? pad : pad + Math.abs(slope) * len;
      var xHead = sx > 0 ? pad + Math.abs(slope) * len : pad;
      var x0 = xTail, y0 = pad;                     /* 尾端（上） */
      var x1 = xHead, y1 = pad + len;               /* 头端（下，粒子坐标所在） */
      /* 🔴 v36 氛围雨的核心：**冷色调 + 极低透明度**（原来头部 0.95 太抢眼，现在 0.20），
         光晕也压到极弱（原来 1.7 倍线宽 ⇒ 现在 0.8）⇒ 远看只是一层"淡蓝斜纹"，近看才是细雨。 */
      var grd = g.createLinearGradient(x0, y0, x1, y1);
      /* 🔴 v37：用户指定「渐变头部透明度 0.8 试试」—— 从 v36 的 0.20 拉回 0.80（中间段同步 0.045→0.18，
         否则会"头很亮、中间骤暗"出现断层）。综合可见度 = 0.80 × 粒子 alpha(0.24~0.54) = 0.19~0.43，
         属于"看得清但比 v35 的 0.95 柔和"的一档。 */
      /* 🔴🔴 v39 **真因修正**（"完全看不到雨"的根因）：雨点原来是**近白色** (232,242,255)，
         而首页 hero 本身就是**白/浅粉底** ⇒ **白雨点落在白底上 = 隐形**
         （实测：最大 alpha 21/255、平均 9/255，且亮像素全集中在浅色区 ⇒ 不是"太淡"，是"同色相遇"）。
         ⇒ 墨色**跟着天色相位走**：白天/拂晓/黄金 = 中灰蓝（浅底可见）；黄昏/夜晚 = 浅蓝白（深底可见）。
         ⚠️ 相位一变必须重建 STREAKS（见 draw() 里的 ph 判断）。 */
      /* 🔴 v43（用户「雨点颜色不喜欢」）：原墨色 86,106,146 画到画面上实测是 rgb(78,87,119)
         —— **灰蓝偏暗**，在浅色 hero 上像"脏灰线"而不是"水"。
         ⇒ 白天系换成**清透的中蓝**（往蓝走、提亮、降灰），夜里系保持浅蓝白不动。 */
      var INK = { day: '112,146,208', dawn: '126,154,206', golden: '134,146,200',
                  dusk: '216,230,255', night: '218,232,255' };
      var ink = INK[st.effPhase] || INK.day;
      /* 🔴 v45（用户「雷暴雨点太浅了」）：雷暴**单独加深**。
         根因：黑云压顶时天色本就暗，雨点却还在用白天那套中蓝（112,146,208）⇒ 在暗背景上"发飘、发浅"。
         ⇒ 雷雨天换成深一档的钢蓝（夜间系本来就该亮，保持不动，甚至更亮一点）。
         ⚠️ 只动雷暴，不牵连 rain / drizzle / snow。 */
      if (kindNow === 'thunder') {
        var DEEP = { day: '58,72,118', dawn: '66,78,120', golden: '74,74,120',
                     dusk: '228,238,255', night: '232,242,255' };
        ink = DEEP[st.effPhase] || DEEP.day;
      }
      grd.addColorStop(0,    'rgba(' + ink + ',0)');
      grd.addColorStop(0.45, 'rgba(' + ink + ',0.34)');
      grd.addColorStop(1,    'rgba(' + ink + ',1)');
      g.strokeStyle = grd;
      g.lineWidth = wid;
      g.lineCap = 'round';
      /* 🔴 v40：**完全不发光晕**。诊断发现 shadowBlur 会把有限的不透明度摊到周围像素上，
         细线的中心 alpha 因此被稀释（实测加粗前后只从 21 涨到 50）。去掉光晕后线更"实"，
         同样亮度下可辨识度明显更高 —— 这也是 Stripe/Linear 那类站点画细线的通用做法。 */
      g.shadowColor = 'rgba(' + ink + ',0)';
      g.shadowBlur = 0;
      g.beginPath(); g.moveTo(x0, y0); g.lineTo(x1, y1); g.stroke();
      out.push({ img: cvs, w: W2, h: H2, ox: x1, oy: y1 });
    }
    STREAKS[kind] = out;
    STREAKS.sc = scale;
    STREAKS.ph = st.effPhase;   /* v39：记下墨色所依据的相位，相位一变就得重建 */
  }

  function loop() {
    if (!raf) return;
    var now = (W.performance && W.performance.now) ? W.performance.now() : Date.now();
    var dt = lastT ? Math.min(0.05, (now - lastT) / 1000) : (1 / 60);
    lastT = now;
    var moved = step(dt);
    stepSplash(dt);
    draw();
    st.frames++;
    if (parts.length) {
      obsSum += (moved / parts.length) / scale;
      obsT += dt;
      if (obsT >= 0.4) { st.obsCss = Math.round(obsSum / obsT); obsSum = 0; obsT = 0; }
    }
    raf = W.requestAnimationFrame(loop);
  }

  function start() { if (raf || !cx) return; lastT = 0; raf = W.requestAnimationFrame(loop); }
  function stop() { if (!raf) return; W.cancelAnimationFrame(raf); raf = 0; }

  function applyKind(k) {
    kindNow = k;
    var p = PLAN[k] || PLAN.clear, i;

    var wantCloud = p.cloud || 0;
    for (i = 0; i < clouds.length; i++) clouds[i].classList.toggle('is-on', i < wantCloud);
    var wantFog = p.fog || 0;
    for (i = 0; i < fogs.length; i++) fogs[i].classList.toggle('is-on', i < wantFog);
    theBolt.classList.toggle('is-on', !!p.bolt && !REDUCE);

    var needParticles = !!p.particles && !REDUCE;
    if (needParticles) {
      seedParts();
      cv.classList.add('is-on');
      start();
    } else {
      stop();
      cv.classList.remove('is-on');
      parts = []; st.obsCss = 0;
      if (cx) cx.clearRect(0, 0, cv.width, cv.height);
    }
    flash.classList.toggle('is-on', !!p.bolt && !REDUCE);
    root.setAttribute('data-hdx-wx', k);
    syncSun();
  }

  /* ------------------------------------------------------------ 自动取数 */
  function readCache() {
    try {
      var raw = W.localStorage.getItem(WX_CK);
      if (!raw) return null;
      var o = JSON.parse(raw);
      if (!o || !o.t || (Date.now() - o.t) > WX_TTL) return null;
      return o.d;
    } catch (e) { return null; }
  }
  function writeCache(d) {
    try { W.localStorage.setItem(WX_CK, JSON.stringify({ t: Date.now(), d: d })); } catch (e) {}
  }

  /* 只留引擎要的几个字段（小、且不把原始大响应塞进 localStorage） */
  function shape(d) {
    var loc = (d && d.location) || {}, cur = (d && d.current) || {};
    var day = ((((d || {}).forecast) || {}).forecastday || [])[0] || {};
    var ast = day.astro || {};
    var m = /(\d{1,2}):(\d{2})\s*$/.exec(String(loc.localtime || ''));
    /* 🔴 v20.1：原来只取了「时:分」，**日期被丢掉了** —— 而"周末 / 节日"文案必须要当地日期+星期。
       localtime 形如 "2026-10-02 14:30"（当地时区）⇒ 取前半段。 */
    var dm = /^(\d{4}-\d{2}-\d{2})/.exec(String(loc.localtime || ''));
    return {
      localDate: dm ? dm[1] : null,
      code:  cur.condition ? cur.condition.code : null,
      text:  cur.condition ? cur.condition.text : '',
      temp:  (cur.temp_c === undefined ? null : cur.temp_c),
      isDay: (cur.is_day === undefined ? null : cur.is_day),
      cloud: (cur.cloud === undefined ? null : cur.cloud),
      place: loc.name ? (loc.name + (loc.country ? ', ' + loc.country : '')) : null,
      localMin: m ? ((+m[1]) * 60 + (+m[2])) : null,
      sun: (ast.sunrise && ast.sunset) ? { sunrise: ast.sunrise, sunset: ast.sunset } : null
    };
  }

  function applyData(o, tag) {
    st.code = o.code; st.temp = o.temp; st.isDay = o.isDay; st.cloud = o.cloud;
    st.place = o.place; st.sun = o.sun;
    st.localMin = o.localMin; st.at = Date.now();
    st.localDate = o.localDate || null;
    st.src = tag;
    if (st.kind === 'auto') applyKind(codeToKind(st.code));
    if (st.phase === 'auto') applyPhase(phaseFromClock(st.sun));
    emit();
  }

  /* 2026-10-27（用户·甲+乙）：定位改为「服务端按访客真实 IP 解析」。
     ----------------------------------------------------------------------------
     背景（实测）：原实现前端直接 `q=auto:ip` 调 WeatherAPI，而它前面是 BunnyCDN
     （`Cache-Control: public, max-age=180`），**缓存键只按 URL、不含访客 IP** ⇒
     每 180 秒里第一个到达者（别国访客/爬虫）决定这一轮所有人看到的城市。
     实测：美国 VPS 用站上同一个 30 分钟 bucket 请求，返回「香港 马草垄」；
     换唯一 buster 才返回它自己的 Los Angeles。
     现在：先问 bridge `/wx/locate`（服务端读 nginx 的 X-Real-IP）拿坐标，
     再 `q=lat,lon` 取天气 ⇒ CDN 缓存键天然按坐标隔离，同城共用、跨城不串。
     locate 不可用时回退旧 `auto:ip`，但 buster 带上「本浏览器会话唯一串」，
     至少不会拿到别人的 IP 结果。 */
  var WX_BRIDGE = 'https://bridge.highdeep.net';
  var WX_SID = '';
  try { WX_SID = W.sessionStorage.getItem('hdxWxSid') || ''; } catch (e) {}
  if (!WX_SID) {
    WX_SID = Math.random().toString(36).slice(2, 10);
    try { W.sessionStorage.setItem('hdxWxSid', WX_SID); } catch (e) {}
  }
  window.__hdWxLocate = null;   /* 调试用：最近一次定位结果（不参与渲染逻辑） */
  function locate() {
    return W.fetch(WX_BRIDGE + '/wx/locate', { cache: 'no-store' })
      .then(function (r) { return r.json(); })
      .then(function (d) {
        if (!d || !d.ok || typeof d.lat !== 'number' || typeof d.lon !== 'number') {
          throw new Error('locate unavailable');
        }
        try { window.__hdWxLocate = d; } catch (e) {}
        return d;
      });
  }

  function fetchLive() {
    /* 🔴 必须带 cache-buster：WeatherAPI 走 BunnyCDN，若缓存被"不带 Origin"的请求
       （curl / 爬虫）先灌满，浏览器拿到的副本会缺 ACAO 头 ⇒ CORS 拦截 ⇒ 天气静默失效。
       2026-10-27：buster 追加 `<坐标>-<本会话唯一串>`，让缓存键按位置与会话隔离。 */
    var bucket = Math.floor(Date.now() / WX_TTL);
    function grab(q, locKey) {
      var url = WX_BASE + '?key=' + encodeURIComponent(WX_KEY) + '&q=' + q +
                '&days=1&aqi=no&alerts=no&_=' + (locKey ? locKey + '-' : '') + bucket + '-' + WX_SID;
      return W.fetch(url, { cache: 'no-store' })
        .then(function (r) { return r.json(); })
        .then(function (d) {
          if (!d || d.error) throw new Error((d && d.error && d.error.message) || 'bad payload');
          return shape(d);
        });
    }
    return locate().then(function (loc) {
      /* 坐标保留 2 位小数（≈1.1km）⇒ 同城同 URL、跨城不同 URL */
      var lat = (Math.round(loc.lat * 100) / 100).toFixed(2);
      var lon = (Math.round(loc.lon * 100) / 100).toFixed(2);
      return grab(lat + ',' + lon, lat + '_' + lon + '-');
    })['catch'](function () {
      return grab('auto%3Aip', '');
    });
  }

  function autoFetch() {
    /* 1) 有缓存先上屏 —— 避免"请求飞行中先显晴天"的跳变 */
    var c = readCache();
    if (c) applyData(c, 'weatherapi(缓存)');
    /* 2) 再取实时；失败时若有缓存就保持缓存，没有才降级 */
    fetchLive().then(function (o) {
      writeCache(o);
      applyData(o, 'weatherapi');
    })['catch'](function () {
      if (c) return;
      st.src = '降级（取数失败）';
      if (st.kind === 'auto') applyKind('clear');
      if (st.phase === 'auto') applyPhase(phaseFromClock(null));
      emit();
    });
  }

  /* ---------------------------------------------------------------- 入口 */
  function init() {
    build();
    /* 2026-10-05 甲批：收起 theme.liquid 里静态输出的 .pf-wx-boot 天色启动层（防跨页白闪）。
       放在 build() 之后、OFF 判断之前 ⇒ 正常 / 「已关闭」两条路径都会收起
       （且 build() 本身在 OFF 判断之前，元素一定已就位）。
       ⚠️ 用属性开关交给 CSS 做 display 硬切，这里不要直接动 style。
       详见 assets/pf-wx.css 的「v49 天色启动层」段。 */
    root.setAttribute('data-hdx-boot', 'done');
    if (OFF) { root.classList.add('pf-wx-off'); st.src = '已关闭'; return; }
    applyPhase(phaseFromClock(null));
    applyKind('clear');
    emit();
    autoFetch();

    D.addEventListener('visibilitychange', function () {
      if (D.hidden) { stop(); }
      else if ((PLAN[kindNow] || {}).particles && !REDUCE) { lastT = 0; start(); }
    });

    var rt = 0;
    W.addEventListener('resize', function () {
      if (!(PLAN[kindNow] || {}).particles || REDUCE) return;
      clearTimeout(rt);
      rt = setTimeout(function () { seedParts(); }, 260);
    });

    setInterval(function () { if (st.phase === 'auto') applyPhase(phaseFromClock(st.sun)); }, 60000);
  }

  W.__hdWx = {
    PHASES: PHASES, KINDS: KINDS, PHASE_CN: PHASE_CN, KIND_CN: KIND_CN, DEMO: DEMO, SPEED: SPEED, PLAN: PLAN,
    CLOUDS: CLOUDS, FOGBANDS: FOGBANDS,
    state: st,
    set: function (key, val) {
      if (key === 'phase') { st.phase = val; applyPhase(val === 'auto' ? phaseFromClock(st.sun) : val); }
      else if (key === 'kind') {
        st.kind = val;
        applyKind(val === 'auto' ? (st.code === null ? 'clear' : codeToKind(st.code)) : val);
      }
      emit();
    },
    refresh: autoFetch,
    on: function (fn) { subs.push(fn); return function () { var i = subs.indexOf(fn); if (i >= 0) subs.splice(i, 1); }; },
    get: function () { return st; }
  };

  if (D.readyState === 'loading') D.addEventListener('DOMContentLoaded', init);
  else init();
})();
